import { useEffect, useState } from 'react'
import { supabase } from '../supabase'
import { useToast } from '../Toast'
import ConfirmModal from '../ConfirmModal'
import { useUser } from '../UserContext'
import { Building2, Eye, EyeOff, RotateCcw, ShieldCheck, Users } from 'lucide-react'

const EMPTY_ORG = { name: '' }

const EMPTY_USER = {
  email: '',
  full_name: '',
  password: '',
  org_id: '',
  role: 'member',
  is_platform_admin: false
}

const LICENSES = {
  starter: { label: 'Starter', limit: 3, annual: '£2,500', monthly: '£250' },
  growth: { label: 'Growth', limit: 10, annual: '£5,000', monthly: '£480' },
  scale: { label: 'Scale', limit: 25, annual: '£8,500', monthly: '£800' },
  enterprise: { label: 'Enterprise', limit: null, annual: 'Custom', monthly: 'Custom' }
}

export default function SystemAdmin() {
  const toast = useToast()
  const { user } = useUser()
  const [organisations, setOrganisations] = useState([])
  const [profiles, setProfiles] = useState([])
  const [licenseUsage, setLicenseUsage] = useState({})
  const [licenseDrafts, setLicenseDrafts] = useState({})
  const [activeTab, setActiveTab] = useState('users')
  const [selectedUserOrgId, setSelectedUserOrgId] = useState('all')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [creatingUser, setCreatingUser] = useState(false)
  const [showUserForm, setShowUserForm] = useState(false)
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null)
  const [confirmHideOrg, setConfirmHideOrg] = useState(null)
  const [removingUserId, setRemovingUserId] = useState(null)
  const [updatingOrgId, setUpdatingOrgId] = useState(null)
  const [savingLicenseOrgId, setSavingLicenseOrgId] = useState(null)
  const [showHiddenOrgs, setShowHiddenOrgs] = useState(false)
  const [orgForm, setOrgForm] = useState(EMPTY_ORG)
  const [userForm, setUserForm] = useState(EMPTY_USER)

  useEffect(() => {
    fetchAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function fetchAll() {
    setLoading(true)

    const [
      { data: orgData, error: orgError },
      { data: profileData, error: profileError },
      { data: usageData, error: usageError }
    ] = await Promise.all([
      supabase.from('organisations').select('*').order('created_at', { ascending: false }),
      supabase.from('profiles').select('id, email, full_name, role, org_id, is_platform_admin').order('email'),
      supabase.rpc('get_organisation_license_usage')
    ])

    if (orgError) toast.error(`Failed to load organisations: ${orgError.message}`)
    if (profileError) toast.error(`Failed to load users: ${profileError.message}`)
    if (usageError) toast.error(`Failed to load licence usage: ${usageError.message}`)

    setOrganisations(orgData || [])
    setProfiles(profileData || [])
    setLicenseUsage(Object.fromEntries((usageData || []).map(item => [item.org_id, item])))
    setLicenseDrafts(Object.fromEntries((orgData || []).map(org => [org.id, {
      license_tier: org.license_tier || '',
      license_billing_cycle: org.license_billing_cycle || 'annual',
      crm_enabled: Boolean(org.crm_enabled)
    }])))
    setLoading(false)
  }

  async function createOrganisation() {
    if (!orgForm.name.trim()) {
      toast.error('Organisation name is required')
      return
    }

    setSaving(true)

    const { error } = await supabase
      .from('organisations')
      .insert([{ name: orgForm.name.trim() }])

    setSaving(false)

    if (error) {
      toast.error(`Failed to create organisation: ${error.message}`)
      return
    }

    toast.success('Organisation created')
    setOrgForm(EMPTY_ORG)
    fetchAll()
  }

  async function createUser() {
    if (!userForm.email.trim()) {
      toast.error('Email is required')
      return
    }

    if (!userForm.password || userForm.password.length < 12) {
      toast.error('Password must be at least 12 characters')
      return
    }

    if (!userForm.org_id) {
      toast.error('Please select an organisation')
      return
    }

    setCreatingUser(true)

    const { data, error } = await supabase.functions.invoke('create-platform-user', {
      body: {
        email: userForm.email.trim().toLowerCase(),
        full_name: userForm.full_name.trim(),
        password: userForm.password,
        org_id: userForm.org_id,
        role: userForm.role,
        is_platform_admin: userForm.is_platform_admin
      }
    })

    setCreatingUser(false)

    if (error) {
      toast.error(`Failed to create user: ${error.message}`)
      return
    }

    if (data?.error) {
      toast.error(`Failed to create user: ${data.error}`)
      return
    }

    toast.success('User created successfully')
    setUserForm(EMPTY_USER)
    setShowUserForm(false)
    fetchAll()
  }

  async function setOrganisationHidden(org, archived) {
    setUpdatingOrgId(org.id)

    const { error } = await supabase
      .from('organisations')
      .update({ archived })
      .eq('id', org.id)

    setUpdatingOrgId(null)

    if (error) {
      toast.error(`Failed to ${archived ? 'hide' : 'restore'} organisation: ${error.message}`)
      return
    }

    setOrganisations(current => current.map(item =>
      item.id === org.id ? { ...item, archived } : item
    ))
    setConfirmHideOrg(null)
    toast.success(`Organisation ${archived ? 'hidden' : 'restored'}`)
  }

  async function updateProfile(profileId, updates) {
    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', profileId)

    if (error) {
      toast.error(`Failed to update user: ${error.message}`)
      return
    }

    toast.success('User updated')
    fetchAll()
  }

  async function saveOrganisationLicense(org) {
    const draft = licenseDrafts[org.id]
    if (!draft) return

    setSavingLicenseOrgId(org.id)
    const { error } = await supabase
      .from('organisations')
      .update({
        license_tier: draft.license_tier || null,
        license_billing_cycle: draft.license_tier ? draft.license_billing_cycle : null,
        crm_enabled: draft.crm_enabled
      })
      .eq('id', org.id)
    setSavingLicenseOrgId(null)

    if (error) {
      toast.error(`Failed to update licence: ${error.message}`)
      return
    }

    toast.success(`${org.name} licence updated`)
    fetchAll()
  }

  async function deleteProfile(profileId) {
    if (profileId === user?.id) {
      toast.error('You cannot remove your own admin user.')
      setConfirmDeleteUser(null)
      return
    }

    setRemovingUserId(profileId)

    const { data, error } = await supabase.functions.invoke('delete-platform-user', {
      body: { user_id: profileId }
    })

    setRemovingUserId(null)

    if (error) {
      toast.error(`Failed to delete user: ${error.message}`)
      return
    }

    if (data?.error) {
      toast.error(`Failed to delete user: ${data.error}`)
      return
    }

    setProfiles(currentProfiles => currentProfiles.filter(profile => profile.id !== profileId))
    setConfirmDeleteUser(null)
    toast.success('User removed')
  }

  const visibleOrganisations = organisations.filter(org => showHiddenOrgs || !org.archived)
  const activeOrganisations = organisations.filter(org => !org.archived)
  const userOrganisationGroups = organisations
    .filter(org => selectedUserOrgId === 'all' ? profiles.some(profile => profile.org_id === org.id) : org.id === selectedUserOrgId)
    .map(org => ({ ...org, users: profiles.filter(profile => profile.org_id === org.id) }))
  const unassignedProfiles = profiles.filter(profile => !organisations.some(org => org.id === profile.org_id))

  if (loading) {
    return (
      <div className="hv-page" style={{ color: 'var(--hv-text-muted)', paddingTop: '60px' }}>
        Loading...
      </div>
    )
  }

  return (
    <div className="hv-page">
      <div className="hv-page-header">
        <div>
          <h1 className="hv-page-title">System Admin</h1>
          <p className="hv-page-subtitle">Manage organisations and platform users</p>
        </div>

        {activeTab === 'users' && (
          <button
            className="hv-btn hv-btn-primary"
            onClick={() => setShowUserForm(!showUserForm)}
          >
            {showUserForm ? 'Cancel' : '+ Add User'}
          </button>
        )}
      </div>

      <div className="system-admin-tabs" role="tablist" aria-label="System administration sections">
        <button className={activeTab === 'users' ? 'active' : ''} onClick={() => setActiveTab('users')}>
          <Users size={16} /> Users
        </button>
        <button className={activeTab === 'organisations' ? 'active' : ''} onClick={() => setActiveTab('organisations')}>
          <Building2 size={16} /> Organisations
        </button>
        <button className={activeTab === 'licensing' ? 'active' : ''} onClick={() => setActiveTab('licensing')}>
          <ShieldCheck size={16} /> Licensing
        </button>
      </div>

      {activeTab === 'users' && showUserForm && (
        <div className="hv-card" style={{ padding: '24px', marginBottom: '24px' }}>
          <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--hv-text-primary)', marginBottom: '20px' }}>
            Add User
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label className="hv-form-label">Full Name</label>
              <input
                className="hv-input"
                value={userForm.full_name}
                onChange={e => setUserForm({ ...userForm, full_name: e.target.value })}
              />
            </div>

            <div>
              <label className="hv-form-label">Email <span style={{ color: 'var(--hv-red)' }}>*</span></label>
              <input
                className="hv-input"
                type="email"
                value={userForm.email}
                onChange={e => setUserForm({ ...userForm, email: e.target.value })}
              />
            </div>

            <div>
              <label className="hv-form-label">Password <span style={{ color: 'var(--hv-red)' }}>*</span></label>
              <input
                className="hv-input"
                type="password"
                value={userForm.password}
                onChange={e => setUserForm({ ...userForm, password: e.target.value })}
              />
            </div>

            <div>
              <label className="hv-form-label">Organisation <span style={{ color: 'var(--hv-red)' }}>*</span></label>
              <select
                className="hv-select"
                value={userForm.org_id}
                onChange={e => setUserForm({ ...userForm, org_id: e.target.value })}
              >
                <option value="">Select organisation</option>
                {activeOrganisations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="hv-form-label">Role</label>
              <select
                className="hv-select"
                value={userForm.role}
                onChange={e => setUserForm({ ...userForm, role: e.target.value })}
              >
                <option value="admin">Admin</option>
                <option value="member">Member</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>

            <div>
              <label className="hv-form-label">Platform Admin</label>
              <select
                className="hv-select"
                value={userForm.is_platform_admin ? 'true' : 'false'}
                onChange={e => setUserForm({ ...userForm, is_platform_admin: e.target.value === 'true' })}
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
          </div>

          <button className="hv-btn hv-btn-primary" onClick={createUser} disabled={creatingUser}>
            {creatingUser ? 'Creating...' : 'Create User'}
          </button>
        </div>
      )}

      {activeTab === 'organisations' && <div className="hv-card" style={{ padding: '24px', marginBottom: '24px' }}>
        <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--hv-text-primary)', marginBottom: '16px' }}>
          Create Organisation
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr auto', gap: '12px' }}>
          <input
            className="hv-input"
            placeholder="e.g. Example Organisation"
            value={orgForm.name}
            onChange={e => setOrgForm({ ...orgForm, name: e.target.value })}
          />

          <button className="hv-btn hv-btn-primary" onClick={createOrganisation} disabled={saving}>
            {saving ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>}

      {activeTab === 'organisations' && <div className="hv-table-wrap" style={{ marginBottom: '24px' }}>
        <div style={{
          padding: '14px 22px',
          borderBottom: '1px solid var(--hv-border)',
          fontSize: '13px',
          fontWeight: '600',
          color: 'var(--hv-text-primary)',
          background: '#FAFBFD',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span>Organisations</span>
          <button
            className="hv-btn hv-btn-secondary"
            style={{ height: '32px', padding: '0 10px', fontSize: '12px' }}
            onClick={() => setShowHiddenOrgs(current => !current)}
          >
            {showHiddenOrgs ? <EyeOff size={14} /> : <Eye size={14} />}
            {showHiddenOrgs ? 'Hide archived' : 'Show hidden'}
          </button>
        </div>

        <table className="hv-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Users</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleOrganisations.map(org => (
              <tr key={org.id}>
                <td className="hv-table-name">{org.name}</td>
                <td style={{ color: 'var(--hv-text-secondary)' }}>
                  {profiles.filter(p => p.org_id === org.id).length}
                </td>
                <td>
                  <span className={`hv-badge ${org.archived ? 'hv-badge-gray' : 'hv-badge-green'}`}>
                    {org.archived ? 'Hidden' : 'Active'}
                  </span>
                </td>
                <td>
                  {org.archived ? (
                    <button
                      className="hv-btn hv-btn-secondary"
                      style={{ height: '32px', padding: '0 10px', fontSize: '12px' }}
                      onClick={() => setOrganisationHidden(org, false)}
                      disabled={updatingOrgId === org.id}
                    >
                      <RotateCcw size={14} />
                      {updatingOrgId === org.id ? 'Restoring...' : 'Restore'}
                    </button>
                  ) : (
                    <button
                      className="hv-btn hv-btn-secondary"
                      style={{ height: '32px', padding: '0 10px', fontSize: '12px' }}
                      onClick={() => setConfirmHideOrg(org)}
                      disabled={updatingOrgId === org.id}
                    >
                      <EyeOff size={14} />
                      Hide
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}

      {activeTab === 'users' && <div className="system-admin-users">
        <div className="system-admin-filter-row">
          <div>
            <label className="hv-form-label" htmlFor="system-admin-org-filter">Organisation</label>
            <select
              id="system-admin-org-filter"
              className="hv-select"
              value={selectedUserOrgId}
              onChange={event => setSelectedUserOrgId(event.target.value)}
            >
              <option value="all">All organisations</option>
              {organisations.map(org => (
                <option key={org.id} value={org.id}>{org.name}{org.archived ? ' (hidden)' : ''}</option>
              ))}
            </select>
          </div>
          <span>{selectedUserOrgId === 'all' ? `${profiles.length} users` : `${userOrganisationGroups[0]?.users.length || 0} users`}</span>
        </div>

        {userOrganisationGroups.map(org => (
          <UserGroup
            key={org.id}
            organisation={org}
            organisations={organisations}
            currentUserId={user?.id}
            removingUserId={removingUserId}
            onUpdate={updateProfile}
            onRemove={setConfirmDeleteUser}
          />
        ))}
        {selectedUserOrgId === 'all' && unassignedProfiles.length > 0 && (
          <UserGroup
            organisation={{ id: 'unassigned', name: 'Unassigned', users: unassignedProfiles }}
            organisations={organisations}
            currentUserId={user?.id}
            removingUserId={removingUserId}
            onUpdate={updateProfile}
            onRemove={setConfirmDeleteUser}
          />
        )}
        {userOrganisationGroups.length === 0 && selectedUserOrgId !== 'all' && (
          <div className="system-admin-empty">No users belong to this organisation.</div>
        )}
      </div>}

      {activeTab === 'licensing' && (
        <div className="hv-table-wrap system-admin-licensing">
          <div className="system-admin-section-header">
            <div>
              <strong>Organisation licensing</strong>
              <span>Assign core deal allowances and the optional CRM module.</span>
            </div>
          </div>
          <table className="hv-table">
            <thead>
              <tr>
                <th>Organisation</th>
                <th>Core licence</th>
                <th>Billing</th>
                <th>Tracked deals</th>
                <th>CRM</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleOrganisations.map(org => {
                const draft = licenseDrafts[org.id] || { license_tier: '', license_billing_cycle: 'annual', crm_enabled: false }
                const plan = LICENSES[draft.license_tier]
                const usage = Number(licenseUsage[org.id]?.tracked_deals || 0)
                const overLimit = plan?.limit != null && usage > plan.limit
                return (
                  <tr key={org.id}>
                    <td>
                      <div className="hv-table-name">{org.name}</div>
                      <div className="hv-table-meta">{org.archived ? 'Hidden organisation' : 'Active organisation'}</div>
                    </td>
                    <td>
                      <select
                        className="hv-select"
                        value={draft.license_tier}
                        onChange={event => setLicenseDrafts(current => ({
                          ...current,
                          [org.id]: { ...draft, license_tier: event.target.value }
                        }))}
                      >
                        <option value="">Legacy / unrestricted</option>
                        {Object.entries(LICENSES).map(([value, details]) => (
                          <option key={value} value={value}>{details.label}</option>
                        ))}
                      </select>
                      <div className="hv-table-meta">
                        {plan ? `${plan.limit == null ? 'Unlimited' : `${plan.limit} deals`} · ${plan[draft.license_billing_cycle]}` : 'No enforced allowance'}
                      </div>
                    </td>
                    <td>
                      <select
                        className="hv-select"
                        disabled={!draft.license_tier}
                        value={draft.license_billing_cycle}
                        onChange={event => setLicenseDrafts(current => ({
                          ...current,
                          [org.id]: { ...draft, license_billing_cycle: event.target.value }
                        }))}
                      >
                        <option value="annual">Annual</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </td>
                    <td>
                      <div className="system-admin-usage">
                        <strong>{usage}</strong>
                        <span>/ {plan?.limit ?? 'Unlimited'}</span>
                      </div>
                      {overLimit && <span className="hv-badge hv-badge-red">Over allowance</span>}
                    </td>
                    <td>
                      <label className="system-admin-toggle">
                        <input
                          type="checkbox"
                          checked={draft.crm_enabled}
                          onChange={event => setLicenseDrafts(current => ({
                            ...current,
                            [org.id]: { ...draft, crm_enabled: event.target.checked }
                          }))}
                        />
                        <span>{draft.crm_enabled ? 'Enabled' : 'Disabled'}</span>
                      </label>
                    </td>
                    <td>
                      <button
                        className="hv-btn hv-btn-primary"
                        style={{ height: '34px', fontSize: '12px' }}
                        disabled={savingLicenseOrgId === org.id}
                        onClick={() => saveOrganisationLicense(org)}
                      >
                        {savingLicenseOrgId === org.id ? 'Saving...' : 'Save'}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {confirmDeleteUser && (
        <ConfirmModal
          title="Remove user"
          message={`Remove ${confirmDeleteUser.full_name || confirmDeleteUser.email} from System Admin? This removes their profile access from the platform.`}
          confirmLabel={removingUserId === confirmDeleteUser.id ? 'Removing...' : 'Remove User'}
          onConfirm={() => {
            if (!removingUserId) deleteProfile(confirmDeleteUser.id)
          }}
          onCancel={() => {
            if (!removingUserId) setConfirmDeleteUser(null)
          }}
        />
      )}

      {confirmHideOrg && (
        <ConfirmModal
          title="Hide organisation"
          message={`Hide ${confirmHideOrg.name} from the active organisation list? Its users and data will not be deleted, and you can restore it later.`}
          confirmLabel={updatingOrgId === confirmHideOrg.id ? 'Hiding...' : 'Hide Organisation'}
          onConfirm={() => {
            if (!updatingOrgId) setOrganisationHidden(confirmHideOrg, true)
          }}
          onCancel={() => {
            if (!updatingOrgId) setConfirmHideOrg(null)
          }}
        />
      )}
    </div>
  )
}

function UserGroup({ organisation, organisations, currentUserId, removingUserId, onUpdate, onRemove }) {
  return (
    <div className="hv-table-wrap system-admin-user-group">
      <div className="system-admin-group-header">
        <div>
          <Building2 size={16} />
          <strong>{organisation.name}</strong>
        </div>
        <span>{organisation.users.length} {organisation.users.length === 1 ? 'user' : 'users'}</span>
      </div>
      <table className="hv-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Organisation</th>
            <th>Role</th>
            <th>Platform Admin</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {organisation.users.map(profile => (
            <tr key={profile.id}>
              <td>
                <div className="hv-table-name">{profile.full_name || profile.email}</div>
                <div className="hv-table-meta">{profile.email}</div>
              </td>
              <td>
                <select
                  className="hv-select"
                  value={profile.org_id || ''}
                  onChange={event => onUpdate(profile.id, { org_id: event.target.value })}
                >
                  <option value="" disabled>Select organisation</option>
                  {organisations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
              </td>
              <td>
                <select
                  className="hv-select"
                  value={profile.role || 'member'}
                  onChange={event => onUpdate(profile.id, { role: event.target.value })}
                >
                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                  <option value="viewer">Viewer</option>
                </select>
              </td>
              <td>
                <select
                  className="hv-select"
                  value={profile.is_platform_admin ? 'true' : 'false'}
                  onChange={event => onUpdate(profile.id, { is_platform_admin: event.target.value === 'true' })}
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              </td>
              <td>
                <button
                  className="hv-btn hv-btn-danger"
                  style={{ fontSize: '12px' }}
                  onClick={() => onRemove(profile)}
                  disabled={removingUserId === profile.id || profile.id === currentUserId}
                >
                  {profile.id === currentUserId ? 'Current User' : removingUserId === profile.id ? 'Removing...' : 'Remove'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
